import React, { useState, useEffect, useContext, useRef } from "react";
import cogoToast from "cogo-toast";
import { useLocation, useHistory } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Input,
  Row,
  Col,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import { FaSearch, FaFileExcel, FaSort, FaSortUp, FaSortDown, FaArrowUp, FaArrowDown, FaMinus } from "react-icons/fa";
import { SUCCESS_RESULT } from "../../Constants";
import { getRatingsByPeriodApi, listScoringPeriodsApi } from "../../services/Api";
import AppContext from "../../AppContext";
import * as XLSX from "xlsx";

const StaffRatingsByPeriod = () => {
  const global = useContext(AppContext);
  const location = useLocation();
  const history = useHistory();
  const [isProcessing, setIsProcessing] = useState(false);
  const [periodsList, setPeriodsList] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [sectorsList, setSectorsList] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [ratingsData, setRatingsData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    fetchPeriods();
  }, []);

  // Handle URL parameter changes when periods are loaded
  useEffect(() => {
    if (periodsList.length === 0) return;
    
    const params = new URLSearchParams(location.search);
    const periodIdFromUrl = params.get('period_id');
    
    if (periodIdFromUrl) {
      const periodFromUrl = periodsList.find(p => p.id === periodIdFromUrl);
      if (periodFromUrl && selectedPeriod !== periodIdFromUrl) {
        setSelectedPeriod(periodIdFromUrl);
      }
    } else if (!selectedPeriod && periodsList.length > 0) {
      // Auto-select first period if no URL parameter and no selection
      setSelectedPeriod(periodsList[0].id);
      history.replace({
        pathname: location.pathname,
        search: `?period_id=${periodsList[0].id}`
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodsList, location.search]);

  // Auto-load ratings when period or sector is selected
  useEffect(() => {
    if (selectedPeriod) {
      fetchRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedSector]);

  // Sync top scrollbar width with table container
  useEffect(() => {
    if (tableContainerRef.current && topScrollbarRef.current && ratingsData) {
      const syncScrollbarWidth = () => {
        if (tableContainerRef.current && topScrollbarRef.current) {
          // Get the actual scroll width of the table container
          const tableScrollWidth = tableContainerRef.current.scrollWidth;
          const topScrollbarContent = topScrollbarRef.current.querySelector('#top-scrollbar-content');
          if (topScrollbarContent) {
            topScrollbarContent.style.width = `${tableScrollWidth}px`;
          }
        }
      };
      
      // Sync after a short delay to ensure table is rendered
      const timeoutId = setTimeout(syncScrollbarWidth, 100);
      
      // Use ResizeObserver to sync when table size changes
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(syncScrollbarWidth, 50);
      });
      
      if (tableContainerRef.current) {
        resizeObserver.observe(tableContainerRef.current);
      }
      
      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    }
  }, [ratingsData]);

  const fetchPeriods = () => {
    listScoringPeriodsApi({ status: 10001 })
      .then((res) => {
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setPeriodsList(response.data || []);
          }
        }
      })
      .catch((e) => {
        console.error("Failed to load periods:", e);
      });
  };

  const fetchRatings = () => {
    if (!selectedPeriod) {
      return;
    }

    setIsProcessing(true);
    const payload = {
      scoring_period_id: selectedPeriod,
    };

    // Sector is no longer used in this view

    getRatingsByPeriodApi(payload)
      .then((res) => {
        setIsProcessing(false);
        if (res.status === 200) {
          const response = res.data;
          if (response.status === SUCCESS_RESULT) {
            setRatingsData(response.data);
          } else {
            cogoToast.error(response.message || "Failed to load ratings", {
              hideAfter: 5,
            });
            setRatingsData(null);
          }
        } else {
          cogoToast.error(res.message || "Failed to load ratings", {
            hideAfter: 5,
          });
          setRatingsData(null);
        }
      })
      .catch((e) => {
        setIsProcessing(false);
        const errorMessage =
          e?.response?.data?.message || "Something went wrong, please try again.";
        cogoToast.error(errorMessage, { hideAfter: 5 });
        setRatingsData(null);
      });
  };

  const handlePeriodChange = (periodId) => {
    setSelectedPeriod(periodId);
    // Update URL with the new period_id
    const params = new URLSearchParams(location.search);
    if (periodId) {
      params.set('period_id', periodId);
    } else {
      params.delete('period_id');
    }
    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : ''
    });
  };

  const exportToExcel = () => {
    if (!ratingsData || !ratingsData.staff_ratings || ratingsData.staff_ratings.length === 0) {
      cogoToast.error("No data to export", { hideAfter: 3 });
      return;
    }

    const exportData = ratingsData.staff_ratings.map((rating) => {
      const row = {
        "Staff ID": rating.staff_staff_id || "",
        "Staff Name": rating.staff_name || "",
        "Department": rating.department_name || "",
        "Designation": rating.designation || "",
        "Total Score": rating.total_score || 0,
        "Weighted Total Score": rating.weighted_total_score || rating.Weighted_Total_Score || 0,
        "Retention Priority": rating.Retention_Priority || "",
        "Final Decision": rating.Final_Decision || "",
        "Status": rating.status_text || "",
        "Remarks": rating.remarks || "",
        "Scored By": rating.scored_by?.name || "",
        "Created At": rating.created_at || "",
        "Updated At": rating.updated_at || "",
      };

      // Add criteria scores as columns
      if (rating.criteria_scores && rating.criteria_scores.length > 0) {
        rating.criteria_scores.forEach((criteriaScore) => {
          row[`${criteriaScore.criteria_name} (Score)`] = criteriaScore.score || 0;
          row[`${criteriaScore.criteria_name} (Weighted)`] = criteriaScore.weighted_score || 0;
        });
      }

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ratings");

    const periodName = ratingsData.scoring_period?.name || "Ratings";
    const fileName = `${periodName}_Ratings_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    cogoToast.success("Ratings exported successfully", { hideAfter: 3 });
  };


  const getStatusBadge = (status, statusText) => {
    if (status === 10001 || statusText === "Submitted") {
      return <Badge color="success">{statusText || "Submitted"}</Badge>;
    } else if (status === 10000 || statusText === "Draft") {
      return <Badge color="warning">{statusText || "Draft"}</Badge>;
    }
    return <Badge color="secondary">{statusText || "Unknown"}</Badge>;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort style={{ marginLeft: "4px", opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp style={{ marginLeft: "4px" }} />
      : <FaSortDown style={{ marginLeft: "4px" }} />;
  };

  const sortedAndFilteredRatings = (() => {
    let filtered = ratingsData?.staff_ratings?.filter((rating) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        (rating.staff_staff_id && rating.staff_staff_id.toLowerCase().includes(search)) ||
        (rating.staff_name && rating.staff_name.toLowerCase().includes(search)) ||
        (rating.department_name && rating.department_name.toLowerCase().includes(search)) ||
        (rating.designation && rating.designation.toLowerCase().includes(search)) ||
        (rating.scored_by?.name && rating.scored_by.name.toLowerCase().includes(search)) ||
        (rating.Retention_Priority && String(rating.Retention_Priority).toLowerCase().includes(search)) ||
        (rating.Final_Decision && String(rating.Final_Decision).toLowerCase().includes(search))
      );
    }) || [];

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'staff_id':
            aValue = a.staff_staff_id || '';
            bValue = b.staff_staff_id || '';
            break;
          case 'staff_name':
            aValue = a.staff_name || '';
            bValue = b.staff_name || '';
            break;
          case 'department':
            aValue = a.department_name || '';
            bValue = b.department_name || '';
            break;
          case 'designation':
            aValue = a.designation || '';
            bValue = b.designation || '';
            break;
          case 'total_score':
            aValue = parseFloat(a.total_score) || 0;
            bValue = parseFloat(b.total_score) || 0;
            break;
          case 'weighted_total_score':
            aValue = parseFloat(a.weighted_total_score || a.Weighted_Total_Score) || 0;
            bValue = parseFloat(b.weighted_total_score || b.Weighted_Total_Score) || 0;
            break;
          case 'retention_priority':
            aValue = a.Retention_Priority || '';
            bValue = b.Retention_Priority || '';
            break;
          case 'final_decision':
            aValue = a.Final_Decision || '';
            bValue = b.Final_Decision || '';
            break;
          case 'status':
            aValue = a.status_text || '';
            bValue = b.status_text || '';
            break;
          case 'scored_by':
            aValue = a.scored_by?.name || '';
            bValue = b.scored_by?.name || '';
            break;
          case 'created_at':
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
            break;
          case 'updated_at':
            aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
            bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
            break;
          default:
            // Handle criteria sorting
            if (sortConfig.key.startsWith('criteria_')) {
              const criteriaId = sortConfig.key.replace('criteria_', '');
              const aScore = a.criteria_scores?.find(cs => cs.criteria_id === criteriaId);
              const bScore = b.criteria_scores?.find(cs => cs.criteria_id === criteriaId);
              aValue = parseFloat(aScore?.score) || 0;
              bValue = parseFloat(bScore?.score) || 0;
            } else {
              return 0;
            }
        }

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  })();

  const renderRetentionPriority = (priority) => {
    if (!priority) return <span style={{ color: "#999" }}>N/A</span>;
    const value = String(priority).toLowerCase();

    if (value === "high" || value === "hight") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #0d47a1, #42a5f5)",
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          <FaArrowUp style={{ fontSize: "12px" }} /> High
        </span>
      );
    }

    if (value === "medium") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #1565c0, #64b5f6)",
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          <FaMinus style={{ fontSize: "12px" }} /> Medium
        </span>
      );
    }

    if (value === "low") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 10px",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #b71c1c, #ef5350)",
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          <FaArrowDown style={{ fontSize: "12px" }} /> Low
        </span>
      );
    }

    return <span style={{ color: "#555", fontWeight: 600 }}>{priority}</span>;
  };

  return (
    <div className="container-fluid">
      <div className="page-header d-print-none mb-4">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Staff Ratings by Period</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="">
        <Card className="shadow-sm">
          <CardHeader>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Scoring Period *</Label>
                  <Input
                    type="select"
                    value={selectedPeriod || ""}
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    disabled={isProcessing}
                  >
                    <option value="">Select Period</option>
                    {periodsList.map((period) => (
                      <option key={period.id} value={period.id}>
                        {period.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Sector (Optional)</Label>
                  <Input
                    type="select"
                    value={selectedSector || ""}
                    onChange={(e) => setSelectedSector(e.target.value || null)}
                    disabled={isProcessing}
                  >
                    <option value="">All Sectors</option>
                    {sectorsList.map((sector) => (
                      <option key={sector.id} value={sector.id}>
                        {sector.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </CardHeader>

          {ratingsData && (
            <CardBody>
              <div className="mb-3">
                <Row className="align-items-center">
                  <Col md={6}>
                    <h5>
                      Period: <strong>{ratingsData.scoring_period?.name}</strong>
                    </h5>
                    {ratingsData.scoring_period?.description && (
                      <p className="text-muted">{ratingsData.scoring_period.description}</p>
                    )}
                  </Col>
                  <Col md={6} className="text-right">
                    <Button
                      color="success"
                      onClick={exportToExcel}
                      className="mr-2"
                    >
                      <FaFileExcel className="mr-2" />
                      Export to Excel
                    </Button>
                  </Col>
                </Row>
              </div>

              {ratingsData.criteria && ratingsData.criteria.length > 0 && (
                <div className="mb-3">
                  <h6>Scoring Criteria:</h6>
                  <Table bordered size="sm" style={{ backgroundColor: "#ffffff" }}>
                    <tbody>
                      {(() => {
                        // Calculate number of columns (3-4 columns)
                        const totalCriteria = ratingsData.criteria.length;
                        const columnsPerRow = totalCriteria <= 3 ? totalCriteria : totalCriteria <= 6 ? 3 : 4;
                        const rows = Math.ceil(totalCriteria / columnsPerRow);
                        
                        const tableRows = [];
                        for (let row = 0; row < rows; row++) {
                          const rowCriteria = [];
                          for (let col = 0; col < columnsPerRow; col++) {
                            const index = row * columnsPerRow + col;
                            if (index < totalCriteria) {
                              const criterion = ratingsData.criteria[index];
                              rowCriteria.push(
                                <td
                                  key={criterion.id}
                                  style={{
                                    padding: "0.75rem",
                                    verticalAlign: "middle",
                                    textAlign: "center",
                                    backgroundColor: "#e7f3ff",
                                    border: "1px solid #b3d9ff"
                                  }}
                                >
                                  <strong>{criterion.name}</strong>
                                  <br />
                                  <small style={{ fontSize: "0.8rem", color: "#666" }}>
                                    Max: {criterion.max_score} | Weight: {criterion.weight_percentage}%
                                  </small>
                                </td>
                              );
                            } else {
                              // Empty cell for alignment
                              rowCriteria.push(
                                <td
                                  key={`empty-${row}-${col}`}
                                  style={{
                                    padding: "0.75rem",
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #dee2e6"
                                  }}
                                ></td>
                              );
                            }
                          }
                          tableRows.push(<tr key={`row-${row}`}>{rowCriteria}</tr>);
                        }
                        return tableRows;
                      })()}
                    </tbody>
                  </Table>
                </div>
              )}

              <div className="mb-3">
                <Input
                  type="text"
                  placeholder="Search by staff ID, name, department, designation, scored by user, retention priority, or final decision..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mb-2"
                />
              </div>

              {/* Top horizontal scrollbar */}
              <div
                ref={topScrollbarRef}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  backgroundColor: "#f0f0f0",
                  borderTop: "1px solid #d0d0d0",
                  borderLeft: "1px solid #d0d0d0",
                  borderRight: "1px solid #d0d0d0",
                  height: "17px",
                  marginBottom: "0"
                }}
                onScroll={(e) => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollLeft = e.target.scrollLeft;
                  }
                }}
              >
                <div id="top-scrollbar-content" style={{ height: "1px" }}></div>
              </div>

              <div 
                ref={tableContainerRef}
                className="table-responsive scoring_designs" 
                style={{ 
                  overflowX: "auto",
                  padding: "10px",
                  borderLeft: "1px solid #d0d0d0",
                  borderRight: "1px solid #d0d0d0",
                  borderBottom: "1px solid #d0d0d0"
                }}
                onScroll={(e) => {
                  if (topScrollbarRef.current) {
                    topScrollbarRef.current.scrollLeft = e.target.scrollLeft;
                  }
                }}
              >
                <Table bordered className="scoring-table">
                  <thead>
                    <tr>
                      <th className="sticky-col-0 col-no">No.</th>
                      <th className="sticky-col-1 col-staff-id" onClick={() => handleSort('staff_id')}>
                        Staff ID {getSortIcon('staff_id')}
                      </th>
                      <th className="sticky-col-2 col-staff-name" onClick={() => handleSort('staff_name')}>
                        Staff Name {getSortIcon('staff_name')}
                      </th>
                      <th onClick={() => handleSort('department')}>Department {getSortIcon('department')}</th>
                      <th onClick={() => handleSort('designation')}>Designation {getSortIcon('designation')}</th>
                      {ratingsData?.criteria?.map((criterion) => (
                        <th
                          key={criterion.id}
                          onClick={() => handleSort(`criteria_${criterion.id}`)}
                        >
                          {criterion.name} {getSortIcon(`criteria_${criterion.id}`)}
                        </th>
                      ))}
                      <th onClick={() => handleSort('total_score')}>
                        Total Score {getSortIcon('total_score')}
                      </th>
                      <th onClick={() => handleSort('weighted_total_score')}>
                        Weighted Total Score {getSortIcon('weighted_total_score')}
                      </th>
                      <th onClick={() => handleSort('retention_priority')}>
                        Retention Priority {getSortIcon('retention_priority')}
                      </th>
                      <th onClick={() => handleSort('final_decision')}>
                        Final Decision {getSortIcon('final_decision')}
                      </th>
                      <th onClick={() => handleSort('status')}>
                        Status {getSortIcon('status')}
                      </th>
                      <th>Remarks</th>
                      <th onClick={() => handleSort('scored_by')}>
                        Scored By {getSortIcon('scored_by')}
                      </th>
                      <th onClick={() => handleSort('created_at')}>
                        Created At {getSortIcon('created_at')}
                      </th>
                      <th onClick={() => handleSort('updated_at')}>
                        Updated At {getSortIcon('updated_at')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndFilteredRatings.length === 0 ? (
                      <tr>
                        <td 
                          colSpan={14 + (ratingsData?.criteria?.length || 0)} 
                          style={{
                            border: "1px solid #000000",
                            padding: "12px",
                            textAlign: "center",
                            backgroundColor: "#ffffff"
                          }}
                        >
                          {searchTerm
                            ? "No ratings found matching your search"
                            : "No ratings available"}
                        </td>
                      </tr>
                    ) : (
                      sortedAndFilteredRatings.map((rating, index) => (
                        <tr key={rating.staff_id}>
                          <td className="sticky-col-0 col-no" style={{ textAlign: "center" }}>
                            {index + 1}
                          </td>
                          <td className="sticky-col-1 col-staff-id" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {rating.staff_staff_id || "N/A"}
                          </td>
                          <td className="sticky-col-2 col-staff-name" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {rating.staff_name || "N/A"}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "left",
                            backgroundColor: "#ffffff",
                            fontSize: "10px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}>{rating.department_name || "N/A"}</td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "left",
                            backgroundColor: "#ffffff",
                            fontSize: "10px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}>{rating.designation || "N/A"}</td>
                          {ratingsData?.criteria?.map((criterion) => {
                            const criteriaScore = rating.criteria_scores?.find(
                              (cs) => cs.criteria_id === criterion.id
                            );
                            return (
                              <td 
                                key={criterion.id} 
                                style={{
                                  border: "1px solid #000000",
                                  padding: "4px 6px",
                                  textAlign: "center",
                                  backgroundColor: "#ffffff",
                                  fontSize: "10px"
                                }}
                              >
                                {criteriaScore ? (
                                  <div>
                                    <strong>{criteriaScore.score || 0}</strong>
                                    {criteriaScore.justification && (
                                      <div style={{ marginTop: "4px" }}>
                                        <small
                                          style={{
                                            fontSize: "10px",
                                            color: "#0066cc",
                                            display: "block",
                                            maxWidth: "150px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            cursor: "help"
                                          }}
                                          title={criteriaScore.justification}
                                        >
                                          {criteriaScore.justification}
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: "#999" }}>-</span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "center",
                            backgroundColor: "#ffffff",
                            fontWeight: "700",
                            fontSize: "12px",
                            color: "#0d47a1"
                          }}>
                            {rating.total_score || 0}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "center",
                            backgroundColor: "#ffffff",
                            fontWeight: "700",
                            fontSize: "12px",
                            color: "#0d47a1"
                          }}>
                            {rating.weighted_total_score || rating.Weighted_Total_Score || 0}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "center",
                            backgroundColor: "#ffffff"
                          }}>
                            {renderRetentionPriority(rating.Retention_Priority)}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "center",
                            backgroundColor: "#ffffff"
                          }}>
                            {rating.Final_Decision || "N/A"}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "center",
                            backgroundColor: "#ffffff"
                          }}>
                            {getStatusBadge(rating.status, rating.status_text)}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "left",
                            backgroundColor: "#ffffff",
                            maxWidth: "200px"
                          }}>
                            {rating.remarks ? (
                              <span title={rating.remarks}>
                                {rating.remarks.length > 50
                                  ? `${rating.remarks.substring(0, 50)}...`
                                  : rating.remarks}
                              </span>
                            ) : (
                              <span style={{ color: "#999" }}>N/A</span>
                            )}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                            padding: "4px 6px",
                            textAlign: "left",
                            backgroundColor: "#ffffff"
                          }}>{rating.scored_by?.name || "N/A"}</td>
                          <td style={{
                            border: "1px solid #000000",
                        padding: "4px 6px",
                            textAlign: "left",
                            backgroundColor: "#ffffff",
                        fontSize: "10px"
                          }}>
                            {rating.created_at
                              ? new Date(rating.created_at).toLocaleString()
                              : "N/A"}
                          </td>
                          <td style={{
                            border: "1px solid #000000",
                        padding: "4px 6px",
                            textAlign: "left",
                            backgroundColor: "#ffffff",
                        fontSize: "10px"
                          }}>
                            {rating.updated_at
                              ? new Date(rating.updated_at).toLocaleString()
                              : "N/A"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          )}
        </Card>
      </div>
    </div>
  );
};

export default StaffRatingsByPeriod;
